import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/NavigationBar';
import NotificationBanner from '../../components/NotificationBanner';
import StatCard from './components/StatCard';
import ActivityItem from './components/ActivityItem';
import QuickActionCard from './components/QuickActionCard';
import NotificationCard from './components/NotificationCard';
import Icon from '../../components/AppIcon';
import { dealsService, appointmentsService, projectsService } from '../../services/crmService';
import { formatIndianShortDate, formatIndianTime } from '../../utils/dateUtils';

const Dashboard = () => {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [urgentNotifications, setUrgentNotifications] = useState([]);

  useEffect(() => {
    loadDashboardData();
    
    const unsubscribeDeals = dealsService?.subscribeToChanges(() => loadDashboardData());
    const unsubscribeAppointments = appointmentsService?.subscribeToChanges(() => loadDashboardData());
    const unsubscribeProjects = projectsService?.subscribeToChanges(() => loadDashboardData());
    
    return () => {
      unsubscribeDeals();
      unsubscribeAppointments();
      unsubscribeProjects();
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [deals, appointments, projects] = await Promise.all([
        dealsService?.getAll(),
        appointmentsService?.getAll(),
        projectsService?.getAll()
      ]);

      const totalDeals = deals?.length;
      const upcomingAppointments = appointments?.filter(apt => apt?.status === 'upcoming')?.length;
      const pendingDeliveries = appointments?.filter(apt => apt?.type === 'delivery' && apt?.status === 'pending')?.length;
      const totalDealValue = deals?.reduce((sum, deal) => sum + (deal?.dealValue || 0), 0);

      setStatsData([
        {
          title: "Total Deals",
          value: totalDeals?.toString(),
          icon: "Briefcase",
          trend: "up",
          trendValue: "+12%",
          color: "primary"
        },
        {
          title: "Upcoming Appointments",
          value: upcomingAppointments?.toString(),
          icon: "Calendar",
          trend: "up",
          trendValue: "+3",
          color: "secondary"
        },
        {
          title: "Pending Deliveries",
          value: pendingDeliveries?.toString(),
          icon: "Package",
          trend: "down",
          trendValue: "-2",
          color: "warning"
        },
        {
          title: "Total Deal Value",
          value: `₹${Math.round(totalDealValue / 1000)}K`,
          icon: "DollarSign",
          trend: "up",
          trendValue: "+18%",
          color: "success"
        }
      ]);

      const recentDeals = deals?.slice(0, 3)?.map(deal => ({
        type: deal?.stage === 'Closed Won' ? 'deal_won' : 'deal_moved',
        client: deal?.clientName,
        value: deal?.dealValue,
        stage: deal?.stage,
        date: formatIndianShortDate(deal?.updatedAt),
        time: formatIndianTime(deal?.updatedAt)
      }));

      const recentAppointments = appointments?.slice(0, 2)?.map(apt => ({
        type: apt?.type === 'appointment' ? 'appointment' : 'delivery',
        client: apt?.client || apt?.project,
        value: apt?.value || 0,
        stage: apt?.status,
        date: formatIndianShortDate(apt?.date),
        time: apt?.time
      }));

      setRecentActivities([...recentDeals, ...recentAppointments]?.slice(0, 5));

      const urgent = appointments?.filter(apt => apt?.status === 'upcoming' || apt?.status === 'pending')?.slice(0, 3)?.map(apt => ({
          type: apt?.type,
          client: apt?.client || apt?.project,
          date: formatIndianShortDate(apt?.date),
          time: apt?.time,
          value: apt?.value || 0
        }));

      setUrgentNotifications(urgent);
      setNotificationCount(urgent?.length);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: "Add New Deal",
      description: "Create a new deal in your pipeline",
      icon: "Plus",
      route: "/pipeline-view",
      color: "primary"
    },
    {
      title: "Add Contact",
      description: "Add a new contact to your CRM",
      icon: "UserPlus",
      route: "/contacts-management",
      color: "secondary"
    },
    {
      title: "View Pipeline",
      description: "Manage your sales pipeline",
      icon: "Kanban",
      route: "/pipeline-view",
      color: "success"
    },
    {
      title: "Projects Timeline",
      description: "Track project deliveries",
      icon: "GanttChart",
      route: "/projects-timeline-view",
      color: "accent"
    }
  ];

  const handleNotificationClick = (notification) => {
    if (notification?.type === 'appointment') {
      navigate('/pipeline-view');
    } else if (notification?.type === 'delivery') {
      navigate('/projects-timeline-view');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      {showBanner && (
        <NotificationBanner
          message="You have 3 appointments and 2 deliveries scheduled within the next 24 hours. Click to view details."
          type="warning"
          onDismiss={() => setShowBanner(false)}
        />
      )}
      <main className="pt-20 px-4 md:px-6 lg:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-2">Dashboard</h1>
                <p className="text-sm md:text-base text-muted-foreground">Welcome back! Here's your business overview</p>
              </div>
              {notificationCount > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 bg-warning/10 border border-warning/30 rounded-xl">
                  <div className="relative">
                    <Icon name="Bell" size={24} className="text-warning" />
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-warning text-warning-foreground text-xs font-semibold rounded-full flex items-center justify-center">
                      {notificationCount}
                    </span>
                  </div>
                  <span className="text-sm md:text-base font-medium text-foreground">Urgent Notifications</span>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading dashboard...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                {statsData?.map((stat, index) => (
                  <StatCard key={index} {...stat} />
                ))}
              </div>

              {/* Quick Actions */}
              <div className="mb-6 md:mb-8">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  {quickActions?.map((action, index) => (
                    <QuickActionCard key={index} {...action} />
                  ))}
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                {/* Urgent Notifications */}
                <div className="lg:col-span-1">
                  <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg md:text-xl font-semibold text-foreground">Urgent Notifications</h2>
                      <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                        <span className="text-sm font-semibold text-warning">{notificationCount}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {urgentNotifications?.map((notification, index) => (
                        <NotificationCard 
                          key={index} 
                          {...notification}
                          onClick={() => handleNotificationClick(notification)}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="lg:col-span-2">
                  <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg md:text-xl font-semibold text-foreground">Recent Activity</h2>
                      <button 
                        onClick={() => navigate('/pipeline-view')}
                        className="text-sm md:text-base text-primary hover:text-primary/80 font-medium transition-smooth flex items-center gap-1"
                      >
                        View All
                        <Icon name="ArrowRight" size={16} />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {recentActivities?.map((activity, index) => (
                        <ActivityItem key={index} {...activity} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Summary */}
              <div className="mt-6 md:mt-8">
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-6 md:p-8">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">Performance Summary</h2>
                      <p className="text-sm md:text-base text-muted-foreground">Your business is performing well this month with strong deal flow and conversion rates.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                      <button 
                        onClick={() => navigate('/pipeline-view')}
                        className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
                      >
                        <Icon name="Kanban" size={20} />
                        View Pipeline
                      </button>
                      <button 
                        onClick={() => navigate('/projects-timeline-view')}
                        className="px-6 py-3 bg-card text-foreground rounded-xl font-medium hover:bg-muted transition-smooth flex items-center justify-center gap-2"
                      >
                        <Icon name="GanttChart" size={20} />
                        View Projects
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;